// @ts-nocheck
import { useMutation } from "@tanstack/react-query";
import type {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import type { AddChild, ChildResponse, UpdateChild } from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Add a child with name and photo. Supports file upload or base64.
 * @summary Add child to session
 */
export const storefrontSessionsChildrenCreate = (
  uuid: string,
  addChild: AddChild,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  const formData = new FormData();
  formData.append("name", addChild.name);
  formData.append("age", addChild.age.toString());
  formData.append("gender", addChild.gender);
  if (addChild.photo !== undefined) {
    formData.append("photo", addChild.photo);
  }
  if (addChild.role_key !== undefined) {
    formData.append("role_key", addChild.role_key);
  }

  return customInstance<ChildResponse>(
    {
      url: `/api/storefront/sessions/${uuid}/children/`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
      signal,
    },
    options
  );
};

export const getStorefrontSessionsChildrenCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>,
    TError,
    { uuid: string; data: AddChild },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>,
  TError,
  { uuid: string; data: AddChild },
  TContext
> => {
  const mutationKey = ["storefrontSessionsChildrenCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>,
    { uuid: string; data: AddChild }
  > = (props) => {
    const { uuid, data } = props ?? {};

    return storefrontSessionsChildrenCreate(uuid, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontSessionsChildrenCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>
>;
export type StorefrontSessionsChildrenCreateMutationBody = AddChild;
export type StorefrontSessionsChildrenCreateMutationError = unknown;

/**
 * @summary Add child to session
 */
export const useStorefrontSessionsChildrenCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>,
    TError,
    { uuid: string; data: AddChild },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontSessionsChildrenCreate>>,
  TError,
  { uuid: string; data: AddChild },
  TContext
> => {
  const mutationOptions =
    getStorefrontSessionsChildrenCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * PUT/DELETE /api/storefront/sessions/{uuid}/children/{child_id}/

Update or remove a child from the session.
 * @summary Update child
 */
export const storefrontSessionsChildrenUpdate = (
  uuid: string,
  childId: string,
  updateChild: UpdateChild,
  options?: SecondParameter<typeof customInstance>
) => {
  const formData = new FormData();
  if (updateChild.name !== undefined) {
    formData.append("name", updateChild.name);
  }
  if (updateChild.age !== undefined) {
    formData.append("age", updateChild.age.toString());
  }
  if (updateChild.gender !== undefined) {
    formData.append("gender", updateChild.gender);
  }
  if (updateChild.photo !== undefined) {
    formData.append("photo", updateChild.photo);
  }
  if (updateChild.photo_base64 !== undefined) {
    formData.append("photo_base64", updateChild.photo_base64);
  }

  return customInstance<ChildResponse>(
    {
      url: `/api/storefront/sessions/${uuid}/children/${childId}/`,
      method: "PUT",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    },
    options
  );
};

export const getStorefrontSessionsChildrenUpdateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>,
    TError,
    { uuid: string; childId: string; data: UpdateChild },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>,
  TError,
  { uuid: string; childId: string; data: UpdateChild },
  TContext
> => {
  const mutationKey = ["storefrontSessionsChildrenUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>,
    { uuid: string; childId: string; data: UpdateChild }
  > = (props) => {
    const { uuid, childId, data } = props ?? {};

    return storefrontSessionsChildrenUpdate(
      uuid,
      childId,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontSessionsChildrenUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>
>;
export type StorefrontSessionsChildrenUpdateMutationBody = UpdateChild;
export type StorefrontSessionsChildrenUpdateMutationError = unknown;

/**
 * @summary Update child
 */
export const useStorefrontSessionsChildrenUpdate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>,
    TError,
    { uuid: string; childId: string; data: UpdateChild },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontSessionsChildrenUpdate>>,
  TError,
  { uuid: string; childId: string; data: UpdateChild },
  TContext
> => {
  const mutationOptions =
    getStorefrontSessionsChildrenUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * PUT/DELETE /api/storefront/sessions/{uuid}/children/{child_id}/

Update or remove a child from the session.
 * @summary Remove child
 */
export const storefrontSessionsChildrenDestroy = (
  uuid: string,
  childId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<void>(
    {
      url: `/api/storefront/sessions/${uuid}/children/${childId}/`,
      method: "DELETE",
    },
    options
  );
};

export const getStorefrontSessionsChildrenDestroyMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>,
    TError,
    { uuid: string; childId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>,
  TError,
  { uuid: string; childId: string },
  TContext
> => {
  const mutationKey = ["storefrontSessionsChildrenDestroy"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>,
    { uuid: string; childId: string }
  > = (props) => {
    const { uuid, childId } = props ?? {};

    return storefrontSessionsChildrenDestroy(uuid, childId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontSessionsChildrenDestroyMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>
>;

export type StorefrontSessionsChildrenDestroyMutationError = unknown;

/**
 * @summary Remove child
 */
export const useStorefrontSessionsChildrenDestroy = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>,
    TError,
    { uuid: string; childId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontSessionsChildrenDestroy>>,
  TError,
  { uuid: string; childId: string },
  TContext
> => {
  const mutationOptions =
    getStorefrontSessionsChildrenDestroyMutationOptions(options);

  return useMutation(mutationOptions);
};
